import type { ReactNode } from 'react';
import { passwordAcceptable } from '@forecastify/shared';
import { ImageField, SetupHeading } from '@forecastify/ui';
import { useI18n, type Translate } from '../../i18n';
import { PasswordRules } from '../password-rules';
import { avatarSeed } from '../identity';
import { SetupField } from './setup-field';
import { IMAGE_TYPES, imageProblem, personInitials } from './setup-rules';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Setup step 1 — who you are: your name, a picture, and your password.      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The onboarding mockup's step 1, plus the password, which the product owner added: *"add the
 * password to the name/profile pic section"*. It is on the invitation page, where the account is
 * made — an invited admin's (four steps) and an invited colleague's (this one only).
 *
 * ⚠️ **The picture is held, not uploaded.** There is no account yet, and for an admin no workspace
 * to file it under until step 2; the caller uploads it when there is (`setup-store.ts`).
 */
export interface ProfileValues {
  readonly family: string;
  readonly given: string;
  readonly avatar: File | null;
  readonly password: string;
  readonly confirmation: string;
}

export const EMPTY_PROFILE: ProfileValues = {
  family: '',
  given: '',
  avatar: null,
  password: '',
  confirmation: '',
};

/** Why the step cannot go on yet, or `null` — the mockup's `blockedKey` for step 1. */
export function profileBlocked(values: ProfileValues, t: Translate): string | null {
  if (values.family.trim() === '' || values.given.trim() === '') return t('setup.profile.blocked');
  if (!passwordAcceptable(values.password, values.confirmation)) {
    return t('setup.profile.blockedPassword');
  }
  return null;
}

export function ProfileStep({
  values,
  onChange,
  avatarSrc,
  avatarError,
  onAvatarError,
  email,
}: {
  readonly values: ProfileValues;
  readonly onChange: (next: ProfileValues) => void;
  readonly avatarSrc: string | null;
  readonly avatarError: string | null;
  readonly onAvatarError: (message: string | null) => void;
  /**
   * The account's address, whose generated face fills the picture until one is uploaded — the
   * same face the sidebar chip draws (`avatarSeed`). Absent or empty keeps the initials.
   */
  readonly email?: string;
}): ReactNode {
  const { t, locale } = useI18n();

  return (
    <>
      <SetupHeading title={t('setup.profile.title')} subtitle={t('setup.profile.subtitle')} />
      <div className="flex flex-col gap-5">
        <ImageField
          shape="circle"
          src={avatarSrc}
          initials={personInitials(values.family, values.given, locale)}
          fallbackIcon="user-round"
          seed={email === undefined || email === '' ? undefined : avatarSeed(email)}
          accept={IMAGE_TYPES.join(',')}
          labels={{
            upload: t('setup.upload'),
            replace: t('setup.replace'),
            remove: t('setup.remove'),
          }}
          hint={t('setup.profile.avatarHint')}
          error={avatarError}
          onPick={(file) => {
            const problem = imageProblem(file);
            if (problem !== null) {
              onAvatarError(
                problem === 'type' ? t('setup.profile.errType') : t('setup.profile.errSize'),
              );
              return;
            }
            onAvatarError(null);
            onChange({ ...values, avatar: file });
          }}
          onRemove={() => {
            onChange({ ...values, avatar: null });
          }}
        >
          <div className="grid w-full min-w-0 gap-3 min-[900px]:grid-cols-2">
            <SetupField
              label={t('setup.profile.family')}
              value={values.family}
              autoComplete="family-name"
              aria-required="true"
              autoFocus
              onChange={(event) => {
                onChange({ ...values, family: event.target.value });
              }}
            />
            <SetupField
              label={t('setup.profile.given')}
              value={values.given}
              autoComplete="given-name"
              aria-required="true"
              onChange={(event) => {
                onChange({ ...values, given: event.target.value });
              }}
            />
          </div>
        </ImageField>

        <div className="flex flex-col gap-3">
          <SetupField
            label={t('setup.profile.password')}
            type="password"
            autoComplete="new-password"
            value={values.password}
            onChange={(event) => {
              onChange({ ...values, password: event.target.value });
            }}
          />
          <SetupField
            label={t('setup.profile.confirm')}
            type="password"
            autoComplete="new-password"
            value={values.confirmation}
            onChange={(event) => {
              onChange({ ...values, confirmation: event.target.value });
            }}
          />
          {/* The same checklist the sign-up and reset screens render — `password-rules.tsx`. */}
          <PasswordRules password={values.password} confirmation={values.confirmation} />
        </div>
      </div>
    </>
  );
}
